import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Image as ImageIcon, SearchX, TriangleAlert } from 'lucide-react';
import type { MediaKind } from '../../shared/types';
import { PageHeader } from '../components/layout';
import { Button, EmptyState, Input, Select, Skeleton } from '../components/ui';
import { MediaGrid } from '../components/media/MediaGrid';
import { UploadDropzone } from '../components/media/UploadDropzone';
import { MediaDetailDialog } from '../components/media/MediaDetailDialog';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/query';
import { debounce } from '../lib/utils';
import { useWorkspaceStore } from '../stores/workspace';

const PAGE_SIZE = 60;

const KIND_OPTIONS = [
  { value: '', label: 'All media' },
  { value: 'image', label: 'Images' },
  { value: 'video', label: 'Video' },
];

/**
 * Media library — owned by WEB-4. Upload dropzone, kind/search filters,
 * a responsive grid, and a detail dialog for alt text and renditions.
 */
export function MediaPage() {
  const navigate = useNavigate();
  const { assetId: routeAssetId } = useParams<{ assetId?: string }>();
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const queryClient = useQueryClient();

  const [kind, setKind] = useState<MediaKind | ''>('');
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(routeAssetId ?? null);

  useEffect(() => {
    if (routeAssetId) setSelectedId(routeAssetId);
  }, [routeAssetId]);

  const commitSearch = useMemo(() => debounce((value: string) => setSearch(value), 300), []);

  const filters = { kind: kind || undefined, search: search || undefined };

  // The library is cursor-paginated server-side; a flat `limit: 60` used to hide
  // everything past the first page with no way to reach it.
  const query = useInfiniteQuery({
    queryKey: qk.mediaList(workspaceId, filters),
    queryFn: ({ pageParam }) =>
      api.media.list({
        ...filters,
        limit: PAGE_SIZE,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: Boolean(workspaceId),
    placeholderData: (previous) => previous,
  });

  const assets = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.assets),
    [query.data],
  );
  const hasFilters = Boolean(kind || search);

  const closeDialog = () => {
    setSelectedId(null);
    if (routeAssetId) navigate('/media', { replace: true });
  };

  const invalidateList = () => {
    void queryClient.invalidateQueries({ queryKey: ['ws', workspaceId, 'media'] });
  };

  return (
    <>
      <PageHeader
        title="Media library"
        description="Images and video for your posts, with per-platform crops and alt text."
      >
        <div className="flex flex-wrap items-center gap-2">
          <Select
            aria-label="Filter by kind"
            selectSize="sm"
            wrapperClassName="w-auto min-w-36"
            options={KIND_OPTIONS}
            value={kind}
            onChange={(event) => setKind(event.target.value as MediaKind | '')}
          />
          <Input
            aria-label="Search media"
            inputSize="sm"
            placeholder="Search by filename or alt text…"
            value={searchText}
            onChange={(event) => {
              setSearchText(event.target.value);
              commitSearch(event.target.value);
            }}
            wrapperClassName="w-full min-w-40 flex-1 sm:w-64 sm:flex-none"
          />
        </div>
      </PageHeader>

      <UploadDropzone
        className="mb-6"
        onUploaded={() => {
          invalidateList();
        }}
      />

      {query.isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="aspect-square w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <EmptyState
          icon={<TriangleAlert className="size-5" />}
          title="Could not load your media"
          description={
            query.error instanceof ApiError ? query.error.message : 'Try refreshing the page.'
          }
          action={
            <Button variant="secondary" onClick={() => void query.refetch()}>
              Try again
            </Button>
          }
        />
      ) : assets.length === 0 ? (
        <EmptyState
          icon={hasFilters ? <SearchX className="size-5" /> : <ImageIcon className="size-5" />}
          title={hasFilters ? 'No media matches these filters' : 'Your library is empty'}
          description={
            hasFilters
              ? 'Try a different kind or search term.'
              : 'Upload images or generate one with AI, then reuse them across platforms with the right crop for each.'
          }
        />
      ) : (
        <>
          <MediaGrid
            assets={assets}
            selectedIds={selectedId ? [selectedId] : []}
            onSelect={(asset) => navigate(`/media/${asset.id}`)}
          />
          {query.hasNextPage && (
            <div className="flex justify-center pt-6">
              <Button
                variant="secondary"
                loading={query.isFetchingNextPage}
                loadingLabel="Loading more media"
                onClick={() => void query.fetchNextPage()}
              >
                Load more
              </Button>
            </div>
          )}
        </>
      )}

      <MediaDetailDialog
        assetId={selectedId}
        onClose={closeDialog}
        onDeleted={() => invalidateList()}
      />
    </>
  );
}

export default MediaPage;
